import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setUnauthorizedHandler } from '../api/client';
import type { Account, AppConfig } from '../api/types';
import { readStoredLocale, useLocale } from '../i18n';
import { detectCountry } from '@/lib/country';

interface SessionValue {
  account: Account | null;
  config: AppConfig | null;
  loading: boolean;
  signIn: (token: string, account: Account) => void;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [account, setAccount] = useState<Account | null>(null);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const { setLocale } = useLocale();

  // Sync the app's locale from the signed-in account's saved preference, but only when the
  // visitor hasn't already made an explicit choice (nothing in localStorage yet).
  const syncLocale = useCallback(
    (next: Account | null) => {
      if (next && readStoredLocale() === undefined) setLocale(next.locale);
    },
    [setLocale],
  );

  const refresh = useCallback(async () => {
    if (!api.token.get()) {
      setAccount(null);
      return;
    }
    try {
      const { account: me } = await api.me.get();
      setAccount(me);
      syncLocale(me);
    } catch {
      setAccount(null);
    }
  }, [syncLocale]);

  useEffect(() => {
    setUnauthorizedHandler(() => setAccount(null));
    let cancelled = false;
    void (async () => {
      const [, loadedConfig] = await Promise.all([
        refresh(),
        api.config(detectCountry()).catch(() => null),
      ]);
      if (!cancelled) {
        setConfig(loadedConfig);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const value = useMemo<SessionValue>(
    () => ({
      account,
      config,
      loading,
      signIn: (token, next) => {
        api.token.set(token);
        setAccount(next);
        syncLocale(next);
        // Prices follow the account's country, which a new account has only just been given.
        void api.config().then(setConfig).catch(() => undefined);
      },
      signOut: () => {
        api.token.set(null);
        setAccount(null);
        void api.config(detectCountry()).then(setConfig).catch(() => undefined);
      },
      refresh,
    }),
    [account, config, loading, refresh, syncLocale],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionValue => {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
};
